import { useMemo } from 'react';

export type CalendarViewMode = 'week' | 'month';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` em horário local (não usar `toISOString`, que desloca o fuso). */
export const formatDateInput = (value: Date): string => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

/** Interpreta `YYYY-MM-DD` como data civil local. Recusa 31/09 e afins. */
export const parseLocalDate = (iso: string): Date | null => {
  const match = String(iso || '').trim().match(ISO_DATE);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

export const addDays = (base: Date, days: number): Date =>
  new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);

export interface CalendarWindow {
  dateFrom: string;
  dateTo: string;
  days: Date[];
  /** Células do mês anterior para alinhar a grade que começa na segunda. */
  leadingBlanks: number;
}

const todayLocal = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const weekWindow = (anchor: Date): CalendarWindow => {
  const weekday = anchor.getDay();
  // getDay() devolve 0 para domingo; a grade começa na segunda.
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const start = addDays(anchor, mondayOffset);
  return {
    dateFrom: formatDateInput(start),
    dateTo: formatDateInput(addDays(start, 6)),
    days: Array.from({ length: 7 }, (_, index) => addDays(start, index)),
    leadingBlanks: 0,
  };
};

const monthWindow = (anchor: Date): CalendarWindow => {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const firstWeekday = start.getDay();
  return {
    dateFrom: formatDateInput(start),
    dateTo: formatDateInput(end),
    days: Array.from({ length: end.getDate() }, (_, index) => addDays(start, index)),
    leadingBlanks: firstWeekday === 0 ? 6 : firstWeekday - 1,
  };
};

/** Converte data de referência + modo de visão na janela exibida na grade. */
export function useCalendarWindow(anchorDate: string, viewMode: CalendarViewMode): CalendarWindow {
  return useMemo(() => {
    const anchor = parseLocalDate(anchorDate) ?? todayLocal();
    return viewMode === 'week' ? weekWindow(anchor) : monthWindow(anchor);
  }, [anchorDate, viewMode]);
}
